import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

type UserRole = 'admin' | 'editor' | 'viewer';

// Options de configuration
type AuthOptions = {
  redirectTo?: string;
};

const defaultOptions: AuthOptions = {
  redirectTo: '/login'
};

// Fonction utilitaire pour le stockage sécurisé
const storage = {
  getItem: (key: string) => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Erreur lors de la lecture du stockage local:', error);
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('Erreur lors de l\'écriture dans le stockage local:', error);
    }
  },
  removeItem: (key: string) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Erreur lors de la suppression du stockage local:', error);
    }
  }
};

export interface User {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string | null;
  role: UserRole;
}

interface AuthResponse {
  success: boolean;
  error?: string;
  redirectTo?: string;
  user?: User;
}

interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signUp: (email: string, password: string, userData: { firstName: string; lastName: string; role?: UserRole }) => Promise<AuthResponse>;
  signOut: (options?: { redirectTo?: string }) => Promise<{ success: boolean; error?: string; redirectTo?: string }>;
  checkUser: (forceCheck?: boolean) => Promise<User | null>;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(() => {
    // Récupérer l'utilisateur depuis le stockage local au chargement initial
    const savedUser = storage.getItem('auth_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const checkingRef = useRef(false);

  const checkUser = useCallback(async (forceCheck = false): Promise<User | null> => {
    // Éviter les appels multiples en parallèle
    if (checkingRef.current && !forceCheck) {
      return user;
    }

    // Marquer qu'une vérification est en cours
    checkingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // 1. Vérifier la session actuelle
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('[useAuth] Erreur de session:', sessionError);
        throw sessionError;
      }

      // 2. Si pas de session, déconnecter et retourner null
      if (!session) {
        if (user) {
          setUser(null);
        }
        return null;
      }

      // 3. Récupérer les informations du profil utilisateur
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (profileError) {
        console.error('[useAuth] Erreur de profil:', profileError);
        
        // Si le profil n'existe pas, déconnecter l'utilisateur
        if (profileError.code === 'PGRST116') {
          console.log('[useAuth] Profil non trouvé, déconnexion...');
          await supabase.auth.signOut();
        }
        
        throw profileError;
      }

      // 4. Récupérer le rôle de l'utilisateur depuis la table user_roles
      let userRole: UserRole = 'viewer';
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .single();

      if (!roleError && roleData) {
        const validRoles = ['admin', 'editor', 'viewer'] as const;
        if (roleData.role && validRoles.includes(roleData.role as UserRole)) {
          userRole = roleData.role as UserRole;
        }
      } else if (roleError && roleError.code !== 'PGRST116') {
        console.error('[useAuth] Erreur lors de la récupération du rôle:', roleError);
      }

      // 5. Créer l'objet utilisateur
      const userProfile: User = {
        id: profileData.id,
        email: session.user.email || profileData.email || '',
        full_name: profileData.full_name || session.user.user_metadata?.full_name || '',
        avatar_url: profileData.avatar_url || session.user.user_metadata?.avatar_url || '',
        phone: profileData.phone || '',
        created_at: profileData.created_at || new Date().toISOString(),
        updated_at: profileData.updated_at || null,
        role: userRole
      };

      // 6. Mettre à jour l'état uniquement si nécessaire
      if (!user || JSON.stringify(user) !== JSON.stringify(userProfile)) {
        setUser(userProfile);
        // Sauvegarder dans le stockage local
        storage.setItem('auth_user', JSON.stringify(userProfile));
      }

      return userProfile;
    } catch (error) {
      console.error('[useAuth] Erreur lors de la vérification de l\'utilisateur:', error);
      setError('Erreur lors de la vérification de l\'utilisateur');
      
      // Ne pas réinitialiser l'utilisateur ici pour éviter les clignotements
      // L'utilisateur sera déconnecté automatiquement si nécessaire
      
      throw error;
    } finally {
      checkingRef.current = false;
      setLoading(false);
    }
  }, [user]);

  const signIn = useCallback(async (email: string, password: string): Promise<AuthResponse> => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[useAuth] Tentative de connexion avec:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (error) {
        console.error('[useAuth] Erreur de connexion:', error);
        setError('Identifiants incorrects');
        return { 
          success: false, 
          error: 'Email ou mot de passe incorrect' 
        };
      }

      if (!data?.user) {
        const errorMsg = 'Aucun utilisateur trouvé après connexion';
        console.error('[useAuth]', errorMsg);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      console.log('[useAuth] Utilisateur connecté, vérification du profil...');
      
      // Attendre que le profil soit chargé
      const userProfile = await checkUser(true);
      
      if (!userProfile) {
        const errorMsg = 'Impossible de charger le profil utilisateur';
        console.error('[useAuth]', errorMsg);
        setError(errorMsg);
        // Déconnexion car le profil est invalide
        await supabase.auth.signOut();
        return { success: false, error: errorMsg };
      }

      console.log('[useAuth] Connexion réussie:', userProfile.email);
      return { success: true };
      
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Une erreur inconnue est survenue';
        
      console.error('[useAuth] Erreur inattendue lors de la connexion:', error);
      setError('Erreur lors de la connexion');
      
      // En cas d'erreur, on s'assure d'être déconnecté
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.error('[useAuth] Erreur lors de la déconnexion après échec de connexion:', signOutError);
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      if (checkingRef.current === false) {
        setLoading(false);
      }
    }
  }, [checkUser]);

  const signUp = useCallback(async (
    email: string, 
    password: string, 
    userData: { firstName: string; lastName: string; role?: UserRole }
  ): Promise<AuthResponse> => {
    try {
      setLoading(true);
      setError(null);

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
          },
        },
      });

      if (signUpError) {
        console.error('[useAuth] Erreur lors de l\'inscription:', signUpError);
        setError(signUpError.message);
        return { success: false, error: signUpError.message };
      }

      if (!authData.user) {
        const errorMsg = 'Aucun utilisateur retourné après l\'inscription';
        console.error('[useAuth]', errorMsg);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .insert([{
          id: authData.user.id,
          email,
          full_name: `${userData.firstName} ${userData.lastName}`.trim(),
          created_at: new Date().toISOString(),
        }]);

      if (profileError) {
        console.error('[useAuth] Erreur lors de la création du profil:', profileError);
        setError(profileError.message);
        return { success: false, error: profileError.message };
      }

      if (userData.role) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert([{
            user_id: authData.user.id,
            role: userData.role,
            created_at: new Date().toISOString(),
          }]);

        if (roleError) {
          console.error('[useAuth] Erreur lors de l\'attribution du rôle:', roleError);
          console.warn('L\'utilisateur a été créé mais le rôle n\'a pas pu être attribué');
        }
      }

      const userWithRole: User = {
        id: authData.user.id,
        email: email,
        full_name: `${userData.firstName} ${userData.lastName}`.trim(),
        avatar_url: null,
        phone: null,
        created_at: new Date().toISOString(),
        updated_at: null,
        role: userData.role || 'viewer'
      };

      setUser(userWithRole);
      // Sauvegarder dans le stockage local
      storage.setItem('auth_user', JSON.stringify(userWithRole));
      return { success: true, user: userWithRole };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Une erreur inconnue est survenue';
      console.error('[useAuth] Erreur lors de l\'inscription:', errorMsg, error);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [checkUser]);

  const signOut = useCallback(async (options: { redirectTo?: string } = {}): Promise<{ success: boolean; error?: string; redirectTo?: string }> => {
    setLoading(true);
    setError(null);
    const redirectTo = options.redirectTo || '/login';

    try {
      // 1. Supprimer les données sensibles du stockage local en premier
      storage.removeItem('auth_user');
      
      // 2. Déconnecter de Supabase Auth
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('[useAuth] Erreur lors de la déconnexion:', error);
        // On continue quand même le processus de déconnexion même en cas d'erreur
      }
      
      // 3. Réinitialiser l'état local
      setUser(null);
      checkingRef.current = false;
      
      console.log('[useAuth] Déconnexion réussie');
      return { 
        success: true,
        redirectTo
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Une erreur inconnue est survenue';
        
      console.error('[useAuth] Erreur inattendue lors de la déconnexion:', error);
      
      // En cas d'erreur, on force la réinitialisation de l'état
      setUser(null);
      storage.removeItem('auth_user');
      checkingRef.current = false;
      
      return { 
        success: false, 
        error: errorMessage,
        redirectTo
      };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let authSubscription: { subscription: { unsubscribe: () => void } } | null = null;
    let authCheckTimeout: NodeJS.Timeout;

    const initializeAuth = async () => {
      if (!isMounted) return;
      
      try {
        console.log('[useAuth] Initialisation de l\'authentification...');
        await checkUser();
      } catch (error) {
        console.error('[useAuth] Erreur lors de l\'initialisation de l\'authentification:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Initialiser l'authentification
    initializeAuth();

    // Configurer l'écouteur des changements d'état d'authentification
    const setupAuthListener = () => {
      try {
        const { data } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (!isMounted) return;
            
            // Ignorer les événements non nécessaires
            if (['INITIAL_SESSION', 'TOKEN_REFRESHED', 'SIGNED_IN', 'USER_UPDATED'].includes(event)) {
              // Ne rien faire pour ces événements qui ne nécessitent pas de rafraîchissement
              if (event === 'SIGNED_IN' && !user) {
                // Seulement vérifier l'utilisateur si on n'a pas encore d'utilisateur
                checkUser(true);
              }
              return;
            }
            
            // Logging d'événement d'authentification désactivé en production

            // Annuler toute vérification en attente
            if (authCheckTimeout) {
              clearTimeout(authCheckTimeout);
            }

            switch (event) {
              case 'SIGNED_OUT':
                // Réinitialiser l'état utilisateur seulement si on était connecté
                if (user) {
                  setUser(null);
                  setError(null);
                  setLoading(false);
                  checkingRef.current = false;
                }
                break;
                
              case 'PASSWORD_RECOVERY':
                // Gérer la récupération de mot de passe si nécessaire
                console.log('[useAuth] Récupération de mot de passe en cours');
                break;
                
              default:
                console.log(`[useAuth] Événement non géré: ${event}`);
            }
          }
        );
        
        authSubscription = data;
        
      } catch (error) {
        console.error('[useAuth] Erreur lors de la configuration de l\'écouteur d\'authentification:', error);
      }
    };

    setupAuthListener();

    return () => {
      isMounted = false;
      
      // Nettoyer le timeout
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout);
      }
      
      // Nettoyer l'abonnement
      if (authSubscription?.subscription) {
        try {
          authSubscription.subscription.unsubscribe();
        } catch (error) {
          console.error('[useAuth] Erreur lors de la suppression de l\'abonnement:', error);
        }
      }
      
      // Réinitialiser l'état de vérification
      checkingRef.current = false;
    };
  }, [checkUser]); // Retrait de user des dépendances pour éviter les boucles

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signOut,
    checkUser,
  };
};

export default useAuth;